import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bell, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const DISMISS_KEY = "vismarks_push_dismissed";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export default function NotificationsOptIn() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user || user === false) return;
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
      if (Notification.permission === "denied" || localStorage.getItem(DISMISS_KEY)) return;
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;
      const existing = await reg.pushManager.getSubscription();
      if (!existing) setShow(true);
    })();
  }, [user]);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { dismiss(); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await api.post("/push/subscribe", sub.toJSON());
      toast.success("Notifications on — you'll hear about hot drops and your orders");
      setShow(false);
    } catch {
      toast.error("Could not enable notifications");
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:w-96 z-40 rounded-xl border border-[#27272A] bg-[#121212]/95 backdrop-blur-md p-4 shadow-2xl shadow-black/60" data-testid="push-optin">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center shrink-0">
          <Bell className="h-5 w-5 text-[#FF3B30]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm text-white tracking-tight">Never miss a drop</div>
          <p className="text-xs text-neutral-400 mt-1">Get alerts when hot products go live and when your orders are confirmed.</p>
          <button onClick={enable} disabled={busy} data-testid="push-enable-btn"
            className="mt-2.5 h-9 px-4 rounded-full bg-[#FF3B30] text-white text-xs font-bold hover:bg-[#e0342a] transition-colors disabled:opacity-50">
            {busy ? "Enabling…" : "Enable notifications"}
          </button>
        </div>
        <button onClick={dismiss} data-testid="push-dismiss-btn" className="text-neutral-500 hover:text-white transition-colors shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
